/**
 * ExchangeRateRequests — money that is recorded but not yet valued.
 *
 * When a contribution arrives in a currency nobody can convert, it is not
 * guessed at and not dropped: it is held here, in the currency it was given in,
 * until someone with finance approval says what it is worth. The rate, where it
 * came from and who approved it are written onto every contribution it settles.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { getErrorMessage } from '@/lib/api';

export const ExchangeRateRequests = () => {
  const qc = useQueryClient();
  const [openFor, setOpenFor] = useState(null);
  const [form, setForm] = useState({ rate: '', effective_date: '', rate_source: '', reason: '' });

  const { data } = useQuery({
    queryKey: ['fx-rate-requests'],
    queryFn: () => api.get('/fx/rate-requests').then((r) => r.data),
    retry: false,
  });

  const approve = useMutation({
    mutationFn: (req) => api.post('/fx/rate-requests/approve', {
      from_currency: req.from_ccy,
      to_currency: req.to_ccy,
      rate: Number(form.rate),
      effective_date: form.effective_date,
      rate_source: form.rate_source,
      reason: form.reason,
    }),
    onSuccess: (r) => {
      toast.success(`Rate approved — ${r.data.settled} contribution${
        r.data.settled === 1 ? '' : 's'} now counted`);
      setOpenFor(null);
      setForm({ rate: '', effective_date: '', rate_source: '', reason: '' });
      ['fx-rate-requests', 'assembly-pledges', 'assembly-leaderboard'].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(getErrorMessage(e,
      'Only a finance approver can set an exchange rate')),
  });

  const requests = data?.requests || [];
  if (!requests.length) return null;

  return (
    <Card className="border-amber-300 bg-amber-50" data-testid="fx-rate-requests">
      <CardContent className="space-y-3 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          An exchange rate is required before this money can be counted
        </p>
        {requests.map((r) => (
          <div key={r.id} className="rounded-lg border border-amber-200 bg-white p-3"
               data-testid={`fx-request-${r.from_ccy}-${r.to_ccy}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex-1 text-sm text-slate-800">
                <strong>{r.from_ccy} → {r.to_ccy}</strong>
                <span className="block text-xs text-slate-500">
                  {r.waiting_count} contribution{r.waiting_count === 1 ? '' : 's'} waiting ·
                  asked for {(r.created_at || '').slice(0, 10)}
                </span>
              </span>
              <Button size="sm" variant="outline"
                      onClick={() => setOpenFor(openFor === r.id ? null : r.id)}
                      data-testid={`fx-approve-open-${r.from_ccy}`}>
                Set the rate
              </Button>
            </div>

            {openFor === r.id ? (
              <div className="mt-3 grid gap-2 border-t border-amber-100 pt-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">1 {r.from_ccy} is worth, in {r.to_ccy}</Label>
                  <Input type="number" step="any" value={form.rate} data-testid="fx-rate-input"
                         onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Effective date</Label>
                  <Input type="date" value={form.effective_date} data-testid="fx-date-input"
                         onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Where this rate came from</Label>
                  <Input value={form.rate_source} placeholder="e.g. Reserve Bank mid-rate"
                         data-testid="fx-source-input"
                         onChange={(e) => setForm((f) => ({ ...f, rate_source: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Why it is being set by hand</Label>
                  <Input value={form.reason} placeholder="e.g. no live feed for this currency"
                         data-testid="fx-reason-input"
                         onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
                </div>
                <div className="sm:col-span-2 flex items-center justify-between gap-2">
                  <p className="text-xs text-slate-500">
                    {Number(form.rate) > 0
                      ? `Every ${r.from_ccy} held will be counted at ${form.rate} ${r.to_ccy}.`
                      : 'The rate is written onto each contribution, with its source and your name.'}
                  </p>
                  <Button size="sm" onClick={() => approve.mutate(r)}
                          disabled={!(Number(form.rate) > 0) || !form.rate_source.trim()
                                    || approve.isPending}
                          data-testid="fx-approve-save">
                    {approve.isPending
                      ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      : <Check className="mr-1.5 h-4 w-4" />}
                    Approve rate
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default ExchangeRateRequests;
